import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';

import CoverPage from './components/cover/CoverPage';
import LoginPage from './components/auth/LoginPage';
import ResetPasswordPage from './components/auth/ResetPasswordPage';
import Dashboard from './components/dashboard/Dashboard';
import PersonalStats from './components/dashboard/PersonalStats';
import LessonsList from './components/lessons/LessonsList';
import LessonDetail from './components/lessons/LessonDetail';
import QuizList from './components/quizzes/QuizList';
import QuizPlay from './components/quizzes/QuizPlay';
import GamesHub from './components/games/GamesHub';
import GamePlayer from './components/games/GamePlayer';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminStudents from './components/admin/AdminStudents';
import AdminCourseContent from './components/admin/AdminCourseContent';
import AdminResources from './components/admin/AdminResources';
import FamilyPage from './components/family/FamilyPage';
import Navbar from './components/common/Navbar';
import ProtectedRoute from './components/common/ProtectedRoute';
import PlatformConsole from './components/platform/PlatformConsole';
import TutorOwl from './components/tutor/TutorOwl';
import EnterReveal from './components/common/EnterReveal';
import LoadingSpinner from './components/common/LoadingSpinner';
import { BRAND } from './components/common/ui';

const PUBLIC_PATHS = ['/', '/login', '/reset-password'];

function AppLayout() {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const isPublicPage = PUBLIC_PATHS.includes(location.pathname);

  if (loading) return <LoadingSpinner fullscreen />;

  return (
    <div style={{ minHeight: '100vh', background: BRAND.navy900 }}>
      {isAuthenticated && !isPublicPage && <Navbar />}
      <Routes>
        <Route path="/" element={<CoverPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        <Route element={<ProtectedRoute role="student" />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/statistiche" element={<PersonalStats />} />
          <Route path="/lezioni" element={<LessonsList />} />
          <Route path="/lezioni/:id" element={<LessonDetail />} />
          <Route path="/quiz" element={<QuizList />} />
          <Route path="/quiz/:id" element={<QuizPlay />} />
          <Route path="/giochi" element={<GamesHub />} />
          <Route path="/giochi/:id" element={<GamePlayer />} />
          <Route path="/famiglia" element={<FamilyPage />} />
        </Route>

        <Route element={<ProtectedRoute role="admin" />}>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/studenti" element={<AdminStudents />} />
          <Route path="/admin/corsi" element={<AdminCourseContent />} />
          <Route path="/admin/lezioni/:id" element={<LessonDetail />} />
          <Route path="/admin/quiz" element={<QuizList />} />
          <Route path="/admin/quiz/:id" element={<QuizPlay />} />
          <Route path="/admin/giochi" element={<GamesHub />} />
          <Route path="/admin/giochi/:id" element={<GamePlayer />} />
          <Route path="/admin/risorse" element={<AdminResources />} />
          <Route path="/admin/famiglia" element={<FamilyPage />} />
        </Route>

        <Route element={<ProtectedRoute role="superadmin" />}>
          <Route path="/platform" element={<PlatformConsole />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {isAuthenticated && !isPublicPage && <TutorOwl />}
      {isAuthenticated && !isPublicPage && <EnterReveal key={location.pathname} />}
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppLayout />
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 4000,
            style: {
              borderRadius: 16,
              padding: '13px 18px',
              fontFamily: BRAND.font,
              fontWeight: 700,
              fontSize: '0.94rem',
              background: BRAND.navy800,
              color: BRAND.ivory,
              border: `1px solid ${BRAND.line}`,
              boxShadow: '0 14px 40px rgba(0,0,0,0.45)',
            },
            success: { iconTheme: { primary: BRAND.gold, secondary: BRAND.navy900 } },
            error: { iconTheme: { primary: BRAND.danger, secondary: BRAND.navy900 }, style: { borderColor: 'rgba(224,122,122,0.5)' } },
          }}
        />
      </AuthProvider>
    </Router>
  );
}

export default App;
